import { Affix, Col, Menu, Row } from 'antd';
import { Tooltip } from 'antd5';
import { Link, graphql, useStaticQuery } from 'gatsby';
import React, { useCallback, useEffect, useState } from 'react';
import { filter, has, isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt';
import SearchIcon from '@material-ui/icons/Search';
import styled from '@emotion/styled';
import { useQuery } from 'react-query';

import {
  AuthLink,
  DashLink,
  HeaderImageContainer,
  HeaderSearch,
  PublicHeaderContainer,
  RightContainer,
  SearchWrapper,
  StyledButton,
  StyledMenu,
  SubMenuItem,
  TopHeaderSection,
} from './header-styles';
import SocialBarComponent from './social-bar';
import MobileNav from './public-header-mobile-nav';

import { Alert } from '../../components';
import { IPublicMenuItem, IPublicSubMenuItem } from '../../types/header-menu-item';
import { LanguageSelector, useI18NContext } from '../../i18n';
import { programShortName } from '../../siteContent';
import { getRealNodes } from '../../utils/contentFilter';
import { isAuthenticated } from '../../state/selectors/auth';
import { navigateTo } from '../../state/actions/routing';
import theme from '../../theme';
import useAutoLogout from '../../hooks/use-auto-logout';
import { logoutUser } from '../utils/common-utils';
import { ADMIN_IDLE_TIMEOUT_SECONDS, USER_IDLE_TIMEOUT_SECONDS } from '../../constants';
import { hasInternalRole } from '../../state/selectors/privilege';
import { getPublicTenantConfig } from '../../apis/tenant';
import { IPublicTenantConfig } from '../../types/models/tenant-management';

const { SubMenu } = Menu;

const HeaderSection = styled.div`
  display: flex;
  width: 100%;
  max-width: ${theme.screenLgMax};
  height: 70px;
  margin: auto;
  justify-content: space-between;
  align-items: center;
`;

const ImageWrapper = styled(Link)`
  vertical-align: middle;
  height: 46px;
  padding-left: 0;

  &:visited {
    color: ${theme.colorsWhite};
  }
`;

const HeaderRightContainer = styled.div`
  white-space: nowrap;
  @media (max-width: ${theme.screenMdMax}) {
    span {
      display: none;
    }
  }
`;

const MiddleContainer = styled.div`
  @media (max-width: ${theme.screenMdMax}) {
    display: none;
  }
  white-space: nowrap;
  min-width: 690px;
`;

const StyledMenuItem = styled(Menu.Item)`
  &&:hover {
    color: ${theme.colorsSecondaryBlue} !important;
  }

  &&:hover::after {
    border-bottom: 2px solid ${theme.colorsSecondaryBlue} !important;
  }
`;

const StyledSubMenu = styled(SubMenu)`
  &&:hover {
    color: ${theme.colorsSecondaryBlue} !important;
  }

  &&:hover::after {
    border-bottom: 2px solid ${theme.colorsSecondaryBlue} !important;
  }
`;

const StyledAlert = styled(Alert)`
  .ant-alert-message {
    font-size: 16px;
  }
`;

const TooltipInnerStyle: React.CSSProperties = {
  boxShadow: '0 0 4px 0 rgba(0,0,0,0.13)',
  border: '1.5px solid #0f7ed3',
  backgroundColor: 'white',
  padding: '13px',
  fontSize: '14px',
  color: 'black',
  whiteSpace: 'pre-wrap',
  borderRadius: '0',
};

export default function PublicHeader() {
  const data = useStaticQuery(
    graphql`
      query {
        header: directusNavigation(navigation_type: { navigation_type_name: { eq: "header" } }) {
          navigation_groups {
            navigation_group_name
            navigation_group_slug
            translations {
              language
              display_name
            }
            is_button
            sort
            navigation_group_items {
              navigation_item_name
              navigation_item_slug
              translations {
                language
                display_name
              }
              sort
            }
          }
        }
        social: allDirectusSocialMedia(sort: { fields: sort }) {
          nodes {
            social_media_link
            social_media_type {
              social_media_name
            }
          }
        }
        logos: directusLogo {
          tenant_logo {
            data {
              full_url
            }
          }
        }
      }
    `
  );
  const { translate, translateSlug } = useI18NContext();
  const dispatch = useDispatch();
  const loggedIn = useSelector(isAuthenticated);
  const [mounted, setMounted] = useState<boolean>(false);
  const [lastScrollingPosition, setScrollingPosition] = useState<number>(0);
  const [headerStyle, setHeaderStyle] = useState({});
  const [search, setSearch] = useState<string>('');
  const isInternalUser = useSelector(hasInternalRole());
  const [publicConfig, setPublicConfig] = useState<IPublicTenantConfig>({
    bannerConfiguration: { isBannerEnabled: false },
  });

  const { data: config } = useQuery(getPublicTenantConfig.QUERY_KEY, () => getPublicTenantConfig(), {
    staleTime: 300000, // 5 minutes
    refetchInterval: 300000,
  });

  useEffect(() => {
    if (!mounted) {
      setMounted(true);
    }
  }, [mounted]);

  useEffect(() => {
    if (config) {
      setPublicConfig(config);
    }
  }, [config]);

  const handlerScroll = useCallback(() => {
    const scrollingPosition = window.pageYOffset || document.documentElement.scrollTop;
    setScrollingPosition(scrollingPosition);

    if (scrollingPosition > lastScrollingPosition && isEmpty(headerStyle)) {
      setHeaderStyle({
        display: 'none',
      });
    } else if (scrollingPosition < lastScrollingPosition && has(headerStyle, 'display')) {
      setHeaderStyle({});
    }
  }, [headerStyle, lastScrollingPosition]);

  useEffect(() => {
    window.addEventListener('scroll', handlerScroll);

    return () => {
      window.removeEventListener('scroll', handlerScroll);
    };
  }, [handlerScroll, lastScrollingPosition]);

  const onPublicMenuItemClick = useCallback(
    (link) => {
      if (link.indexOf('http') === 0) {
        window.location.href = link;
        return;
      }
      dispatch(navigateTo(link) as any);
    },
    [dispatch]
  );

  const filteredButtons = filter(data.header.navigation_groups, (item) => {
    return item.is_button;
  });
  const filteredMenuItems = filter(data.header.navigation_groups, (item) => {
    return !item.is_button;
  });

  const renderPublicMenu = () => {
    const menuItems = filteredMenuItems.map((item: IPublicMenuItem) => {
      if (item.navigation_group_items.length === 0) {
        return (
          <StyledMenuItem
            onClick={() => onPublicMenuItemClick(translateSlug(item.navigation_group_slug))}
            key={item.navigation_group_name}
          >
            {translate(item).display_name}
          </StyledMenuItem>
        );
      }

      const subMenuItem = item.navigation_group_items.map((subItem: IPublicSubMenuItem) => {
        return (
          <SubMenuItem
            key={subItem.navigation_item_name}
            onClick={() => onPublicMenuItemClick(translateSlug(subItem.navigation_item_slug))}
          >
            <Tooltip
              overlayInnerStyle={TooltipInnerStyle}
              placement={'top'}
              arrow={false}
              title={translate(subItem).display_name.length > 20 && translate(subItem).display_name}
            >
              <span>{translate(subItem).display_name}</span>
            </Tooltip>
          </SubMenuItem>
        );
      });

      return (
        <StyledSubMenu title={translate(item).display_name} key={item.navigation_group_name}>
          {subMenuItem}
        </StyledSubMenu>
      );
    });

    return menuItems;
  };

  const renderButtons = () => {
    const buttons = filteredButtons.map((item: IPublicMenuItem) => {
      return (
        <StyledButton
          type="primary-blue"
          onClick={() => onPublicMenuItemClick(translateSlug(item.navigation_group_slug))}
          key={item.navigation_group_name}
        >
          {translate(item).display_name}
        </StyledButton>
      );
    });

    return buttons;
  };

  const renderAuthLinks = useCallback(() => {
    // do not build static markup for ssr
    if (!mounted) {
      return null;
    }

    if (loggedIn) {
      return (
        <DashLink to="/myhsf/dashboard">
          <span>My Dashboard</span>
          <ArrowRightAltIcon />
        </DashLink>
      );
    }

    return (
      <>
        <AuthLink to="/sign-up">Sign Up</AuthLink>
        <AuthLink to="/login">
          <AccountCircleIcon />
          <span>Sign In</span>
        </AuthLink>
      </>
    );
  }, [loggedIn, mounted]);

  const searchHandler = useCallback(({ value }) => {
    setSearch(value);
  }, []);

  const searchRedirectHandler = (event: any) => {
    if (event.key === 'Enter' && search !== '' && search !== undefined) {
      const route = `${translateSlug('/search')}?query=${encodeURIComponent(search)}`;
      dispatch(navigateTo(route) as any);
    }
  };

  const socialMediaItems = getRealNodes(data.social);
  const tenantLogoUrl = data?.logos?.tenant_logo?.data?.full_url;

  const logout = useCallback(async () => logoutUser(dispatch), [dispatch]);
  useAutoLogout(isInternalUser ? ADMIN_IDLE_TIMEOUT_SECONDS : USER_IDLE_TIMEOUT_SECONDS, loggedIn, logout);

  return (
    <Affix offsetTop={0}>
      {publicConfig?.bannerConfiguration?.isBannerEnabled && (
        <StyledAlert
          type="warning"
          showIcon
          message={publicConfig.bannerConfiguration.title}
          description={publicConfig.bannerConfiguration.description}
        />
      )}
      <PublicHeaderContainer>
        <TopHeaderSection style={headerStyle} align="middle" justify="space-between">
          <SearchWrapper xs={0} lg={10}>
            <HeaderSearch
              size="large"
              value={search}
              onChange={searchHandler}
              placeholder="Search"
              onKeyDown={searchRedirectHandler}
              prefix={<SearchIcon />}
            />
          </SearchWrapper>
          <RightContainer xs={0} lg={12}>
            <Row>
              <Col xs={0} lg={10}>
                <SocialBarComponent socialItems={socialMediaItems} isHeader={true} />
              </Col>
              <Col xs={0} lg={4}>
                <LanguageSelector />
              </Col>
              <Col xs={0} lg={10}>
                {renderAuthLinks()}
              </Col>
            </Row>
          </RightContainer>
        </TopHeaderSection>
        <HeaderSection>
          <ImageWrapper to="/">
            {tenantLogoUrl && <HeaderImageContainer src={tenantLogoUrl} alt={`to ${programShortName} Home`} />}
          </ImageWrapper>
          <MiddleContainer>
            <StyledMenu mode="horizontal">{renderPublicMenu()}</StyledMenu>
          </MiddleContainer>
          <HeaderRightContainer>
            <span>{renderButtons()}</span>
            <Row>
              <Col md={24} lg={0}>
                <MobileNav menu={data.header.navigation_groups} handleClick={onPublicMenuItemClick} />
              </Col>
            </Row>
          </HeaderRightContainer>
        </HeaderSection>
      </PublicHeaderContainer>
    </Affix>
  );
}
